import MerchantPaymentGateways from '../components/MerchantPaymentGateways';
import MerchantProviderCsvActions from '../components/MerchantProviderCsvActions';
import MerchantFinancialDashboard, { MerchantStatusBadge } from '../components/MerchantFinancialDashboard';
import './MerchantView.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceTabNavigation } from '../context/WorkspaceTabsContext';
import merchantService from '../services/merchantService';
import gameService from '../services/gameService';
import changeHistoryService from '../services/changeHistoryService';
import ticketService from '../services/ticketService';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import FilterDropdown from '../components/FilterDropdown';
import ChangeHistoryTimeline from '../components/ChangeHistoryTimeline';
import { ArrowLeftIcon, EditIcon, SearchIcon, CopyIcon } from '../components/Icons';
import {
  getCredentialKey,
  getEffectiveCredentialValue,
  hasEffectiveCredentialValue,
  normalizeCredentialFields
} from '../utils/credentialFields';
import {
  formatCountryOptionLabel,
  formatCurrencyOptionLabel
} from '../utils/gameLabels';

const getHistoryTime = (entry) => new Date(entry.created_at || 0).getTime();

const compareHistoryEntries = (left, right) => {
  if (left.request_id && left.request_id === right.request_id) {
    const leftSequence = left.request_sequence ?? Number.MAX_SAFE_INTEGER;
    const rightSequence = right.request_sequence ?? Number.MAX_SAFE_INTEGER;
    if (leftSequence !== rightSequence) return leftSequence - rightSequence;
  }

  const timeDiff = getHistoryTime(left) - getHistoryTime(right);
  if (timeDiff !== 0) return timeDiff;

  return String(left._id || '').localeCompare(String(right._id || ''));
};

const sortHistoryTimeline = (entries = []) => [...entries].sort(compareHistoryEntries);

const MerchantView = () => {
  const navigate = useNavigate();
  const { closeCurrentTabAndNavigate } = useWorkspaceTabNavigation();
  const { id } = useParams();
  const { hasPermission, getDefaultRoute } = useAuth();
  const [loading, setLoading] = useState(true);
  const [merchant, setMerchant] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const canView = hasPermission('merchants', 'view');
  const canEdit = hasPermission('merchants', 'edit');
  const canExport = hasPermission('merchants', 'export');
  const canViewProviderFinancial = hasPermission('merchants', 'view_financial');
  const canViewChangeHistory = hasPermission('audit_logs', 'view');
  const canViewTickets = hasPermission('tickets', 'view');
  const canViewFinance = hasPermission('finance_reconciliation', 'view');

  // Providers Data
  const [providers, setProviders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [providersLoading, setProvidersLoading] = useState(false);
  const [envSections, setEnvSections] = useState({ staging: false, production: false });
  const [providerDetailsOpen, setProviderDetailsOpen] = useState({});
  const [showProviderFilters, setShowProviderFilters] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  // Filter Options
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Filter State
  const [providerSearchTerm, setProviderSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubCategory, setFilterSubCategory] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [catRes, subRes, srcRes, countryRes, currencyRes] = await Promise.all([
        gameService.getCategories({ limit: 1000 }),
        gameService.getSubCategories({ limit: 1000 }),
        gameService.getSources({ limit: 1000 }),
        gameService.getCountries({ limit: 1000, is_active: true }),
        gameService.getCurrencies({ limit: 1000, is_active: true })
      ]);
      if (catRes.success) setCategories(catRes.data);
      if (subRes.success) setSubCategories(subRes.data);
      if (srcRes.success) setSources(srcRes.data);
      if (countryRes.success) setCountries(countryRes.data);
      if (currencyRes.success) setCurrencies(currencyRes.data);
    } catch (err) {
      console.error('Failed to fetch filter options', err);
    }
  }, []);

  const fetchMerchant = useCallback(async () => {
    try {
      setLoading(true);
      const res = await merchantService.getMerchantById(id);
      if (res.success) {
        setMerchant(res.data);
      } else {
        alert(res.message || 'Failed to fetch merchant details');
        navigate('/merchants');
      }
    } catch (err) {
      console.error('Error fetching merchant:', err);
      alert('An error occurred while fetching merchant details');
      navigate('/merchants');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchProviders = useCallback(async (page = 1, limit = pagination.limit) => {
    try {
      setProvidersLoading(true);
      const params = {
        page,
        limit,
        search: providerSearchTerm,
        type: filterType,
        source: filterSource,
        country: filterCountry,
        currency: filterCurrency,
        category: filterCategory,
        sub_category: filterSubCategory
      };

      const res = await merchantService.getMerchantProviders(id, params);
      if (res.success) {
        setProviders(res.data);
        setPagination(res.pagination);
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
    } finally {
      setProvidersLoading(false);
    }
  }, [id, pagination.limit, providerSearchTerm, filterType, filterSource, filterCountry, filterCurrency, filterCategory, filterSubCategory]);

  const fetchChangeHistory = useCallback(async () => {
    if (!canViewChangeHistory) return;
    try {
      setHistoryLoading(true);
      const res = await changeHistoryService.getHistory({
        page: 1,
        limit: 50,
        related_module: 'merchants',
        related_document_id: id
      });
      if (res.success) {
        setHistoryEntries(sortHistoryTimeline(res.data || []));
      }
    } catch (err) {
      console.error('Error fetching merchant change history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [canViewChangeHistory, id]);

  const fetchTickets = useCallback(async () => {
    if (!canViewTickets) return;
    try {
      setTicketsLoading(true);
      const res = await ticketService.getMerchantTickets(id);
      if (res.success) setTickets(res.data);
    } catch (err) {
      console.error('Error fetching setup tickets:', err);
    } finally {
      setTicketsLoading(false);
    }
  }, [canViewTickets, id]);

  useEffect(() => {
    if (!canView) {
      navigate(getDefaultRoute(), { replace: true });
      return;
    }
    fetchMerchant();
    fetchTickets();
  }, [fetchMerchant, fetchTickets, canView, getDefaultRoute, navigate]);

  useEffect(() => {
    if (canView && activeTab === 'products') fetchFilterOptions();
  }, [canView, activeTab, fetchFilterOptions]);

  useEffect(() => {
    if (!canView || activeTab !== 'products') return;
    setProvidersLoading(true);
    const timer = setTimeout(() => {
      fetchProviders(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [canView, activeTab, providerSearchTerm, filterType, filterSource, filterCountry, filterCurrency, filterCategory, filterSubCategory, fetchProviders]);

  const handleCopy = (text, label) => {
    if (!canExport) return;
    navigator.clipboard.writeText(text).then(() => {
      alert(`${label} copied to clipboard!`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const renderCredentialValue = (value, label) => {
    const text = String(value || '').trim();

    if (!text) {
      return <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bg-soft)', padding: '0.4rem 0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', gap: '0.75rem' }}>
        <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{text}</span>
        <button onClick={() => handleCopy(text, label)} style={{ flex: '0 0 auto', background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.25rem' }} title={`Copy ${label}`}>
          <CopyIcon size={14} />
        </button>
      </div>
    );
  };

  const isEnvComplete = (envCreds, credentialKeys = [], callbackDefs = []) => {
    if (!envCreds) return false;
    const boUrls = Array.isArray(envCreds.bo_urls)
      ? envCreds.bo_urls
      : (envCreds.bo_url ? [envCreds.bo_url] : []);
    if (boUrls.length === 0 || boUrls.some(u => !u || !u.trim())) return false;
    const apiUrls = Array.isArray(envCreds.api_urls)
      ? envCreds.api_urls
      : (envCreds.api_url ? [envCreds.api_url] : []);
    if (apiUrls.length === 0 || apiUrls.some(u => !u || !u.trim())) return false;
    for (const field of normalizeCredentialFields(credentialKeys)) {
      if (!hasEffectiveCredentialValue(envCreds, field)) return false;
    }
    for (const cb of callbackDefs) {
      const val = envCreds.callback_paths?.[cb.name] || cb.path;
      if (!val || !String(val).trim()) return false;
    }
    return true;
  };

  const providerTypes = useMemo(() => ['Seamless', 'Transfer'], []);

  const resolveCallbackUrl = (apiBaseUrl, callbackPath) => {
    const rawPath = String(callbackPath || '').trim();
    if (!rawPath) return '';
    if (/^https?:\/\//i.test(rawPath)) return rawPath;

    const base = String(apiBaseUrl || '').trim();
    if (!base) return rawPath;

    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = rawPath.replace(/^\/+/, '');
    return `${normalizedBase}/${normalizedPath}`;
  };

  const truncateUrlMiddle = (url, maxLength = 40, headLength = 18, tailLength = 12) => {
    const value = String(url || '').trim();
    if (!value || value.length <= maxLength) return value;
    return `${value.slice(0, headLength)}...${value.slice(-tailLength)}`;
  };

  if (loading) {
    return <div className="page-container" style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  if (!merchant) {
    return <div className="page-container" style={{ padding: '2rem', textAlign: 'center' }}>Merchant not found.</div>;
  }

  const resetProviderFilters = () => {
    setProviderSearchTerm('');
    setFilterType('');
    setFilterCategory('');
    setFilterSubCategory('');
    setFilterSource('');
    setFilterCountry('');
    setFilterCurrency('');
  };
  const showProducts = name => {
    if (typeof name === 'string') {
      resetProviderFilters();
      setProviderSearchTerm(name);
    }
    setActiveTab('products');
  };
  const advancedFilterCount = [filterCountry, filterCurrency, filterCategory, filterSubCategory].filter(Boolean).length;
  const formatEnvironmentSummary = env => (
    env.totalUrls + env.totalKeys + env.totalCallbacks === 0
      ? 'None configured'
      : `U ${env.filledUrls}/${env.totalUrls} · K ${env.filledKeys}/${env.totalKeys} · C ${env.filledCallbacks}/${env.totalCallbacks}`
  );

  return (
    <div className="merchant-view-page">
      <div className="merchant-page-actions">
        <button className="btn" onClick={() => closeCurrentTabAndNavigate('/merchants')} style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ArrowLeftIcon />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {canViewChangeHistory && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setHistoryOpen(true);
                fetchChangeHistory();
              }}
            >
              Change History
            </button>
          )}
          {canEdit && (
            <button className="btn btn-primary" onClick={() => navigate(`/merchants/${id}`)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <EditIcon size={16} />
              Edit Merchant
            </button>
          )}
        </div>
      </div>

      <Modal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={`Change History: ${merchant.name}`}
        maxWidth="980px"
      >
        <ChangeHistoryTimeline
          entries={historyEntries}
          loading={historyLoading}
          emptyMessage="No merchant change history yet."
          showSequence
        />
      </Modal>

      <header className="card merchant-identity">
        <div>
          <div className="merchant-identity-title"><h1>{merchant.name}</h1><MerchantStatusBadge status={merchant.is_active ? 'active' : 'inactive'} /></div>
          <div className="merchant-identity-meta"><span>IT <code>{merchant.merchant_code}</code></span><span>Finance <code>{merchant.client_code || 'Not set'}</code></span></div>
        </div>
        <div className="merchant-identity-location"><span>{merchant.country?.name || 'Country not set'}</span><span>{merchant.currency?.currency_code || merchant.currency?.code || 'Currency not set'}</span></div>
      </header>

      <nav className="merchant-view-tabs" aria-label="Merchant sections">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'products', label: `Products (${merchant.providers?.length || 0})` },
          { key: 'payment-gateways', label: `Payment Gateways (${merchant.payment_gateways?.length || 0})` },
          ...(canViewFinance ? [{ key: 'financial', label: 'Financial Reports' }] : []),
          { key: 'configuration', label: 'Configuration' }
        ].map(tab => <button key={tab.key} type="button" aria-pressed={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>{tab.label}</button>)}
      </nav>

      <MerchantFinancialDashboard
        key={id}
        merchant={merchant}
        view={activeTab}
        canViewFinance={canViewFinance}
        tickets={tickets}
        ticketsLoading={ticketsLoading}
        canViewTickets={canViewTickets}
        onViewProducts={showProducts}
        onViewPaymentGateways={() => setActiveTab('payment-gateways')}
        onViewReports={() => setActiveTab('financial')}
      />

      {activeTab === 'configuration' && <>
      <div className="card merchant-config-summary">
        <span>VPN <strong>{merchant.is_client_vpn ? 'Yes' : 'No'}</strong></span>
        <span>SMS OTP <strong>{merchant.is_client_sms_otp ? 'Yes' : 'No'}</strong></span>
        <span>Email OTP <strong>{merchant.is_client_email_otp ? 'Yes' : 'No'}</strong></span>
        <span>Created <strong>{new Date(merchant.created_at).toLocaleString()}</strong></span>
        <span>Updated <strong>{new Date(merchant.updated_at).toLocaleString()}</strong></span>
      </div>

      <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.25rem', color: 'var(--text-primary)' }}>Cloudflare Name Servers</h3>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Nameservers assigned to this merchant domain.</p>
          </div>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', borderRadius: '9999px', padding: '0.25rem 0.65rem', whiteSpace: 'nowrap' }}>
            {merchant.cloudflare_name_servers?.length || 0} configured
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
          {merchant.cloudflare_name_servers?.length > 0 ? merchant.cloudflare_name_servers.map((server, i) => (
            <div key={`cloudflare-ns-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', padding: '0.75rem 0.9rem' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 650, color: 'var(--text-primary)', wordBreak: 'break-all' }}>{server}</span>
              <button onClick={() => handleCopy(server, 'Name server')} style={{ flex: '0 0 auto', background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.2rem' }} title="Copy name server">
                <CopyIcon size={14} />
              </button>
            </div>
          )) : (
            <div style={{ gridColumn: '1 / -1', color: 'var(--text-muted)', fontSize: '0.875rem', fontStyle: 'italic' }}>No Cloudflare name servers configured.</div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
        {/* Staging Info */}
        <div className="card" style={{ overflow: 'hidden' }}>
          <button
            type="button"
            className="btn"
            onClick={() => setEnvSections(prev => ({ ...prev, staging: !prev.staging }))}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.5rem',
              borderRadius: 0,
              backgroundColor: 'var(--bg-soft)',
              borderBottom: '1px solid var(--border-light)'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700 }}>
              <span style={{ padding: '0.25rem 0.5rem', backgroundColor: 'var(--warning)', color: '#000', borderRadius: '4px', fontSize: '0.7rem' }}>STAGING</span>
              Environment Infrastructure
            </span>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{envSections.staging ? 'Hide' : 'Show'}</span>
          </button>
          {envSections.staging && (
            <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Frontend URLs</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {merchant.staging_frontend_urls?.length > 0 ? merchant.staging_frontend_urls.map((url, i) => (
                    <div key={`stg-f-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bg-soft)', padding: '0.4rem 0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{url}</span>
                      <button onClick={() => handleCopy(url, 'Frontend URL')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.25rem' }} title="Copy URL">
                        <CopyIcon size={14} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Backend URLs</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {merchant.staging_backend_urls?.length > 0 ? merchant.staging_backend_urls.map((url, i) => (
                    <div key={`stg-b-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bg-soft)', padding: '0.4rem 0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{url}</span>
                      <button onClick={() => handleCopy(url, 'Backend URL')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.25rem' }} title="Copy URL">
                        <CopyIcon size={14} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>API URLs</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {merchant.staging_api_urls?.length > 0 ? merchant.staging_api_urls.map((url, i) => (
                    <div key={`stg-api-url-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bg-soft)', padding: '0.4rem 0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{url}</span>
                      <button onClick={() => handleCopy(url, 'API URL')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.25rem' }} title="Copy URL">
                        <CopyIcon size={14} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>API Key</label>
                  {renderCredentialValue(merchant.staging_api_key, 'Staging API Key')}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>API Secret</label>
                  {renderCredentialValue(merchant.staging_api_secret, 'Staging API Secret')}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>BO IPs</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {merchant.staging_server_ips?.length > 0 ? merchant.staging_server_ips.map((ip, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'var(--bg-soft)', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{ip}</span>
                      <button onClick={() => handleCopy(ip, 'IP')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem' }}>
                        <CopyIcon size={12} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>API IPs</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {merchant.staging_api_ips?.length > 0 ? merchant.staging_api_ips.map((ip, i) => (
                    <div key={`stg-api-ip-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'var(--bg-soft)', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{ip}</span>
                      <button onClick={() => handleCopy(ip, 'IP')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem' }}>
                        <CopyIcon size={12} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Client IPs (Whitelist)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {merchant.staging_client_ips?.length > 0 ? merchant.staging_client_ips.map((ip, i) => (
                    <div key={`stg-client-ip-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'var(--bg-soft)', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{ip}</span>
                      <button onClick={() => handleCopy(ip, 'IP')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem' }}>
                        <CopyIcon size={12} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
            </div>
            </div>
          )}
        </div>

        {/* Production Info */}
        <div className="card" style={{ overflow: 'hidden' }}>
          <button
            type="button"
            className="btn"
            onClick={() => setEnvSections(prev => ({ ...prev, production: !prev.production }))}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.5rem',
              borderRadius: 0,
              backgroundColor: 'var(--bg-soft)',
              borderBottom: '1px solid var(--border-light)'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700 }}>
              <span style={{ padding: '0.25rem 0.5rem', backgroundColor: 'var(--success)', color: '#fff', borderRadius: '4px', fontSize: '0.7rem' }}>PRODUCTION</span>
              Environment Infrastructure
            </span>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{envSections.production ? 'Hide' : 'Show'}</span>
          </button>
          {envSections.production && (
            <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Frontend URLs</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {merchant.production_frontend_urls?.length > 0 ? merchant.production_frontend_urls.map((url, i) => (
                    <div key={`prd-f-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bg-soft)', padding: '0.4rem 0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{url}</span>
                      <button onClick={() => handleCopy(url, 'Frontend URL')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.25rem' }} title="Copy URL">
                        <CopyIcon size={14} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Backend URLs</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {merchant.production_backend_urls?.length > 0 ? merchant.production_backend_urls.map((url, i) => (
                    <div key={`prd-b-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bg-soft)', padding: '0.4rem 0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{url}</span>
                      <button onClick={() => handleCopy(url, 'Backend URL')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.25rem' }} title="Copy URL">
                        <CopyIcon size={14} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>API URLs</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {merchant.production_api_urls?.length > 0 ? merchant.production_api_urls.map((url, i) => (
                    <div key={`prd-api-url-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bg-soft)', padding: '0.4rem 0.75rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{url}</span>
                      <button onClick={() => handleCopy(url, 'API URL')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.25rem' }} title="Copy URL">
                        <CopyIcon size={14} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>API Key</label>
                  {renderCredentialValue(merchant.production_api_key, 'Production API Key')}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>API Secret</label>
                  {renderCredentialValue(merchant.production_api_secret, 'Production API Secret')}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>BO IPs</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {merchant.production_server_ips?.length > 0 ? merchant.production_server_ips.map((ip, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'var(--bg-soft)', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{ip}</span>
                      <button onClick={() => handleCopy(ip, 'IP')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem' }}>
                        <CopyIcon size={12} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>API IPs</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {merchant.production_api_ips?.length > 0 ? merchant.production_api_ips.map((ip, i) => (
                    <div key={`prd-api-ip-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'var(--bg-soft)', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{ip}</span>
                      <button onClick={() => handleCopy(ip, 'IP')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem' }}>
                        <CopyIcon size={12} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Client IPs (Whitelist)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {merchant.production_client_ips?.length > 0 ? merchant.production_client_ips.map((ip, i) => (
                    <div key={`prd-client-ip-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'var(--bg-soft)', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border-light)' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{ip}</span>
                      <button onClick={() => handleCopy(ip, 'IP')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem' }}>
                        <CopyIcon size={12} />
                      </button>
                    </div>
                  )) : <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>None configured</span>}
                </div>
              </div>
            </div>
            </div>
          )}
        </div>
      </div>

      </>}

      {activeTab === 'payment-gateways' && (
        <MerchantPaymentGateways value={merchant.payment_gateways || []} readOnly canViewFinancial={canViewProviderFinancial} />
      )}

      {activeTab === 'products' && (

      <section className="card merchant-products-section">
        <div className="merchant-products-heading">
          <h3>Product Configuration <span>{providersLoading ? 'Loading…' : `${pagination.total} ${pagination.total === 1 ? 'connection' : 'connections'}`}</span></h3>
          <small>Compare products, upstream sources and rates. Expand a row for environment details.</small>
        </div>
        <div style={{ marginBottom: '1rem' }}><MerchantProviderCsvActions merchantId={id} merchantName={merchant.name}
          onImported={async () => {
            const response = await merchantService.getMerchantById(id);
            if (response.success) setMerchant(response.data);
            await fetchProviders(1);
          }} /></div>
        <div className="merchant-products-toolbar">
          <div className="search-container">
            <div className="search-icon-wrapper"><SearchIcon /></div>
            <input type="search" className="search-input" aria-label="Search products" placeholder="Search product name…" value={providerSearchTerm} onChange={event => setProviderSearchTerm(event.target.value)} />
          </div>
          <FilterDropdown value={filterType} onChange={setFilterType} placeholder="All Types" options={providerTypes.map(type => ({ value: type, label: type }))} />
          <FilterDropdown value={filterSource} onChange={setFilterSource} placeholder="All Sources" options={sources.map(source => ({ value: source._id, label: source.name }))} />
          <button type="button" className="btn btn-secondary" aria-expanded={showProviderFilters} aria-controls="merchant-product-filters" onClick={() => setShowProviderFilters(value => !value)}>More filters{advancedFilterCount ? ` (${advancedFilterCount})` : ''}</button>
          <button type="button" className="btn btn-secondary" onClick={resetProviderFilters}>Reset</button>
        </div>
        {showProviderFilters && <div id="merchant-product-filters" className="merchant-products-filters">
          <div><label>Country</label><FilterDropdown value={filterCountry} onChange={setFilterCountry} placeholder="All Countries" options={countries.map(country => ({ value: country._id, label: formatCountryOptionLabel(country) }))} /></div>
          <div><label>Currency</label><FilterDropdown value={filterCurrency} onChange={setFilterCurrency} placeholder="All Currencies" options={currencies.map(currency => ({ value: currency._id, label: formatCurrencyOptionLabel(currency) }))} /></div>
          <div><label>Category</label><FilterDropdown value={filterCategory} onChange={setFilterCategory} placeholder="All Categories" options={categories.map(category => ({ value: category._id, label: category.name }))} /></div>
          <div><label>Sub Category</label><FilterDropdown value={filterSubCategory} onChange={setFilterSubCategory} placeholder="All Sub Categories" options={subCategories.map(category => ({ value: category._id, label: category.name }))} /></div>
        </div>}

        <div style={{ position: 'relative' }}>
          {providersLoading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 1
            }}>
              Loading...
            </div>
          )}
          {providers.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              {providersLoading ? 'Loading providers...' : 'No providers found matching your filters.'}
            </div>
          ) : (
            <div className="merchant-table-scroll">
              <table className={`table-striped merchant-products-table${canViewProviderFinancial ? '' : ' merchant-products-table--basic'}`}>
                <thead><tr>
                  <th>Shortcode</th><th>Unique ID</th><th>Product</th><th>Upline / Source</th><th>Market</th><th>Status</th>
                  {canViewProviderFinancial && <><th>Line</th><th className="merchant-rate">Cost</th><th className="merchant-rate">Sell</th><th className="merchant-rate">Margin</th></>}
                  <th title="U: URLs · K: Credential keys · C: Callbacks. Filled / total.">Configuration</th><th><span className="merchant-sr-only">Environment details</span></th>
                </tr></thead>
                <tbody>
              {providers.map((p, idx) => {
                const masterProvider = p.provider_id;
                const providerKey = masterProvider?._id || `${idx}`;
                const costingRate = masterProvider?.costing_rates || 0;
                const effectiveCostingRate = p.is_client_line ? 0 : costingRate;
                const sellingRate = p.selling_rate || 0;
                const margin = sellingRate - effectiveCostingRate;
                const status = p.status || (p.is_active === true ? 'active' : 'draft');
                const providerName = masterProvider?.provider_id?.name || 'Unknown';
                const sourceType = masterProvider?.type || '-';
                const sourceName = masterProvider?.source?.name || '-';
                const categoryName = masterProvider?.provider_id?.category?.name || '-';
                const subCategoryName = masterProvider?.provider_id?.sub_category?.name || '-';
                const credentialKeys = normalizeCredentialFields(masterProvider?.api_credential_keys);
                const boCredentialKeys = normalizeCredentialFields(masterProvider?.bo_credential_keys);
                const callbackDefs = Array.isArray(p.callback_urls)
                  ? p.callback_urls
                  : (Array.isArray(masterProvider?.callback_urls) ? masterProvider.callback_urls : []);
                const detailsOpen = !!providerDetailsOpen[providerKey];

                const envSummary = (env) => {
                  const envCreds = p.credentials?.[env] || {};
                  const allKeys = [...credentialKeys, ...boCredentialKeys];
                  const boUrls = Array.isArray(envCreds.bo_urls)
                    ? envCreds.bo_urls
                    : (envCreds.bo_url ? [envCreds.bo_url] : []);
                  const apiUrls = Array.isArray(envCreds.api_urls)
                    ? envCreds.api_urls
                    : (envCreds.api_url ? [envCreds.api_url] : []);
                  const merchantEnvApiUrls = Array.isArray(merchant?.[`${env}_api_urls`])
                    ? merchant[`${env}_api_urls`]
                    : [];
                  const primaryApiUrl = merchantEnvApiUrls.length > 0 ? merchantEnvApiUrls[0] : '';
                  const totalUrls = boUrls.length + apiUrls.length;
                  const filledUrls = [...boUrls, ...apiUrls].filter(url => !!String(url || '').trim()).length;
                  const totalKeys = allKeys.length;
                  const filledKeys = allKeys.filter(key => hasEffectiveCredentialValue(envCreds, key)).length;
                  const callbackEntries = callbackDefs.length > 0
                    ? callbackDefs.map(cb => {
                      const path = envCreds?.callback_paths?.[cb.name] || cb.path || '';
                      return {
                        name: cb.name,
                        path,
                        fullUrl: resolveCallbackUrl(primaryApiUrl, path)
                      };
                    })
                    : Object.keys(envCreds.callback_paths || {}).map(name => {
                      const path = envCreds.callback_paths?.[name] || '';
                      return {
                        name,
                        path,
                        fullUrl: resolveCallbackUrl(primaryApiUrl, path)
                      };
                    });
                  const totalCallbacks = callbackEntries.length;
                  const filledCallbacks = callbackEntries.filter(cb => cb.path).length;
                  return { envCreds, callbackEntries, totalUrls, filledUrls, totalKeys, filledKeys, totalCallbacks, filledCallbacks };
                };

                const production = envSummary('production');
                const staging = envSummary('staging');

                return (
                  <React.Fragment key={providerKey}>
                    <tr className={detailsOpen ? 'merchant-product-expanded' : ''}>
                      <td className="merchant-product-identifier">{masterProvider?.shortcode || '—'}</td>
                      <td className="merchant-product-identifier">{masterProvider?.code || '—'}</td>
                      <td><strong>{providerName}</strong><small>{categoryName}{subCategoryName !== '-' ? ` / ${subCategoryName}` : ''}</small></td>
                      <td>{sourceName}<small>{sourceType}</small></td>
                      <td>{masterProvider?.currency?.currency_code || masterProvider?.currency?.code || '—'}<small>{masterProvider?.country?.code || masterProvider?.country?.name || '—'}</small></td>
                      <td><MerchantStatusBadge status={status} /></td>
                      {canViewProviderFinancial && <>
                        <td>{p.is_client_line ? 'Client' : 'Company'}</td>
                        <td className="merchant-rate">{effectiveCostingRate}%</td>
                        <td className="merchant-rate">{sellingRate}%</td>
                        <td className="merchant-rate" style={{ color: margin > 0 ? 'var(--success)' : margin < 0 ? 'var(--error)' : 'var(--text-secondary)' }}>{margin > 0 ? '+' : ''}{margin.toFixed(2)}%</td>
                      </>}
                      <td className="merchant-product-config" title="U: URLs · K: Credential keys · C: Callbacks. Filled / total."><span><b>PROD</b> {formatEnvironmentSummary(production)}</span><span><b>STG</b> {formatEnvironmentSummary(staging)}</span></td>
                      <td><button type="button" className="merchant-row-details" aria-expanded={detailsOpen} aria-controls={`provider-environment-${providerKey}`} aria-label={`${detailsOpen ? 'Hide' : 'Show'} ${providerName} environment details`} onClick={() => setProviderDetailsOpen(prev => ({ ...prev, [providerKey]: !detailsOpen }))}>{detailsOpen ? 'Hide' : 'Details'}</button></td>
                    </tr>

                    {detailsOpen && (
                      <tr className="merchant-product-detail-row table-detail-row" id={`provider-environment-${providerKey}`}><td colSpan={canViewProviderFinancial ? 12 : 8}>
                      <div className="merchant-product-environments">
                        {[
                          { key: 'production', label: 'Production', data: production },
                          { key: 'staging', label: 'Staging', data: staging }
                        ].map(({ key: envKey, label, data }) => {
                          const envReady = isEnvComplete(data.envCreds, [...credentialKeys, ...boCredentialKeys], callbackDefs);
                          const boUrls = Array.isArray(data.envCreds?.bo_urls)
                            ? data.envCreds.bo_urls
                            : (data.envCreds?.bo_url ? [data.envCreds.bo_url] : []);
                          const apiUrls = Array.isArray(data.envCreds?.api_urls)
                            ? data.envCreds.api_urls
                            : (data.envCreds?.api_url ? [data.envCreds.api_url] : []);
                          return (
                            <div key={`${providerKey}-${envKey}`} style={{ backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.75rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.55rem' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{label}</div>
                                <span style={{
                                  padding: '0.15rem 0.5rem',
                                  borderRadius: '9999px',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  backgroundColor: envReady ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                  color: envReady ? 'var(--success)' : 'var(--error)'
                                }}>{envReady ? 'READY' : 'MISSING'}</span>
                              </div>

                              <div style={{ marginBottom: '0.6rem' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>Backoffice URLs</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                  {boUrls.filter(url => !!String(url || '').trim()).map((url, idx) => (
                                    <div key={`${envKey}-bo-url-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.4rem', backgroundColor: 'var(--bg-card)', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border-light)', alignItems: 'center' }}>
                                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{url}</span>
                                      {canExport && (
                                        <button
                                          onClick={() => handleCopy(url, `${envKey.toUpperCase()} BO URL`)}
                                          style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem', display: 'flex', alignItems: 'center', borderRadius: '4px' }}
                                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(var(--primary-rgb), 0.1)'}
                                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                          title="Copy"
                                        >
                                          <CopyIcon size={12} />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  {boUrls.filter(url => !!String(url || '').trim()).length === 0 && (
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>None configured</span>
                                  )}
                                </div>
                              </div>

                              <div style={{ marginBottom: '0.6rem' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>BO Credentials</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem' }}>
                                  {boCredentialKeys.map(field => {
                                    const key = getCredentialKey(field);
                                    const val = getEffectiveCredentialValue(data.envCreds, field);
                                    if (!val) return null;
                                    const displayVal = canExport ? (val.length > 8 ? `${val.substring(0, 8)}...` : val) : '••••••••';
                                    return (
                                      <div key={`${envKey}-bo-${key}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.4rem', backgroundColor: 'var(--bg-card)', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border-light)', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{key}</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                          <span style={{ fontSize: '0.72rem', color: 'var(--text-primary)' }}>{displayVal}</span>
                                          {canExport && (
                                            <button
                                              onClick={() => handleCopy(val, `${envKey.toUpperCase()} BO ${key}`)}
                                              style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem', display: 'flex', alignItems: 'center', borderRadius: '4px' }}
                                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(var(--primary-rgb), 0.1)'}
                                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                              title="Copy"
                                            >
                                              <CopyIcon size={12} />
                                            </button>
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}
                                  {boCredentialKeys.filter(key => hasEffectiveCredentialValue(data.envCreds, key)).length === 0 && (
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>None configured</span>
                                  )}
                                </div>
                              </div>

                              <div style={{ marginBottom: '0.6rem' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>API URLs</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                  {apiUrls.filter(url => !!String(url || '').trim()).map((url, idx) => (
                                    <div key={`${envKey}-api-url-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.4rem', backgroundColor: 'var(--bg-card)', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border-light)', alignItems: 'center' }}>
                                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{url}</span>
                                      {canExport && (
                                        <button
                                          onClick={() => handleCopy(url, `${envKey.toUpperCase()} API URL`)}
                                          style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem', display: 'flex', alignItems: 'center', borderRadius: '4px' }}
                                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(var(--primary-rgb), 0.1)'}
                                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                          title="Copy"
                                        >
                                          <CopyIcon size={12} />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  {apiUrls.filter(url => !!String(url || '').trim()).length === 0 && (
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>None configured</span>
                                  )}
                                </div>
                              </div>

                              <div style={{ marginBottom: '0.6rem' }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>API Credentials</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem' }}>
                                  {credentialKeys.map(field => {
                                    const key = getCredentialKey(field);
                                    const val = getEffectiveCredentialValue(data.envCreds, field);
                                    if (!val) return null;
                                    const displayVal = canExport ? (val.length > 8 ? `${val.substring(0, 8)}...` : val) : '••••••••';
                                    return (
                                      <div key={`${envKey}-api-${key}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.4rem', backgroundColor: 'var(--bg-card)', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border-light)', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{key}</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                          <span style={{ fontSize: '0.72rem', color: 'var(--text-primary)' }}>{displayVal}</span>
                                          {canExport && (
                                            <button
                                              onClick={() => handleCopy(val, `${envKey.toUpperCase()} ${key}`)}
                                              style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem', display: 'flex', alignItems: 'center', borderRadius: '4px' }}
                                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(var(--primary-rgb), 0.1)'}
                                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                              title="Copy"
                                            >
                                              <CopyIcon size={12} />
                                            </button>
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}
                                  {credentialKeys.filter(key => hasEffectiveCredentialValue(data.envCreds, key)).length === 0 && (
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>None configured</span>
                                  )}
                                </div>
                              </div>

                              <div>
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>Callback URLs</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                  {data.callbackEntries.filter(cb => cb.path).map(cb => (
                                    <div key={`${envKey}-cb-${cb.name}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.4rem', backgroundColor: 'var(--bg-card)', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border-light)', alignItems: 'center' }}>
                                      <span style={{ fontSize: '0.72rem', color: 'var(--text-primary)' }}>{cb.name}</span>
                                      {(() => {
                                        const callbackValue = cb.fullUrl || cb.path;
                                        const callbackDisplay = truncateUrlMiddle(callbackValue);
                                        return (
                                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }} title={callbackValue}>{callbackDisplay}</span>
                                        {canExport && (
                                          <button
                                            onClick={() => handleCopy(callbackValue, `${envKey.toUpperCase()} ${cb.name} Callback`)}
                                            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: '0.1rem', display: 'flex', alignItems: 'center', borderRadius: '4px' }}
                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(var(--primary-rgb), 0.1)'}
                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            title="Copy"
                                          >
                                            <CopyIcon size={12} />
                                          </button>
                                        )}
                                      </span>
                                        );
                                      })()}
                                    </div>
                                  ))}
                                  {data.callbackEntries.length === 0 && (
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>None configured</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: '1rem' }}>
            <Pagination
              pagination={pagination}
              onPageChange={(pg) => fetchProviders(pg)}
              onLimitChange={(l) => {
                setPagination(prev => ({ ...prev, limit: l }));
                fetchProviders(1, l);
              }}
              loading={providersLoading}
              itemName="providers"
            />
          </div>
        </div>
      </section>
      )}
    </div>
  );
};

export default MerchantView;
